import sortBy from 'lodash/sortBy';

import {
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_EQUATION_RESPONSE,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_MULTIPLE_CHOICE,
  ACTIVITIES_COMPONENT_TYPE_COLUMNS,
} from 'global/constants';
import type { GetActivitiesSectionsQuery } from '../lesson-builder.graphql.generated';
import { Answer, CheckAnswerData, checkAnswerVar } from './check-answer.apollo';
import {
  ComponentColumns,
  ComponentEquationResponse,
  ComponentMultipleChoice,
} from '../components/component.constants';
import { calculateAnswer } from '../components/equation-response/equation-response.utils';
import { ANSWER_LABELS } from '../components/multiple-choice/multiple-choice.constants';
import type { Pluck } from 'lib/gql/pluck';

export function isCorrect({
  answer,
  correctAnswer,
  ignoreCase,
}: {
  answer: string | number | boolean | undefined;
  correctAnswer: string | number | boolean;
  ignoreCase?: boolean;
}) {
  if (
    typeof answer === 'string' &&
    typeof correctAnswer === 'string' &&
    ignoreCase
  ) {
    return answer.toLowerCase() === correctAnswer.toLowerCase();
  } else {
    return answer === correctAnswer;
  }
}

export function isMultiAnswerCorrect({
  answerRefs,
  correctRefs,
}: {
  answerRefs: string[];
  correctRefs: string[];
}) {
  let allCorrect = true;
  for (let i = 0; i < answerRefs.length; i++) {
    if (!correctRefs.includes(answerRefs[i])) {
      allCorrect = false;
    }
  }
  return allCorrect;
}

function convertComponentToCheckAnswer(
  component: Pluck<
    GetActivitiesSectionsQuery,
    'getActivitiesSections.components'
  >
): Answer | undefined {
  if (
    component.type === ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_MULTIPLE_CHOICE
  ) {
    const multipleChoiceComp = (component as ComponentMultipleChoice).properties
      .multipleChoice;
    const sortedOptions = sortBy(multipleChoiceComp.options, 'sort');
    const correctAnswer = sortedOptions.findIndex(o => o.correctAnswerBoolean);

    const correctLabels = [];
    for (let i = 0; i < sortedOptions.length; i++) {
      if (sortedOptions[i].correctAnswerBoolean) {
        correctLabels.push(ANSWER_LABELS[i]);
      }
    }

    return {
      id: component.id,
      sectionId: component.sectionId,
      answer: undefined,
      correctAnswer: ANSWER_LABELS[correctAnswer],
      multiAnswer: {
        answerRefs: [],
        correctLabels,
        correctRefs: multipleChoiceComp.options
          .filter(o => o.correctAnswerBoolean)
          .map(o => o.itemId || ''),
      },
    };
  } else if (
    component.type === ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_EQUATION_RESPONSE
  ) {
    const equationComp = (component as ComponentEquationResponse).properties;
    const correctAnswer =
      calculateAnswer(
        equationComp.firstOperand || 0,
        equationComp.secondOperand || 0,
        equationComp.operation
      ) || 0;

    return {
      id: component.id,
      sectionId: component.sectionId,
      answer: undefined,
      correctAnswer,
    };
  }
}

export function initialiseAnswerData(
  components: Pluck<
    GetActivitiesSectionsQuery,
    'getActivitiesSections.components'
  >[]
) {
  const currentAnswerData = checkAnswerVar();
  checkAnswerVar({
    ...currentAnswerData,
    ...getCheckAnswerData(Object.keys(currentAnswerData), components),
  });
}

export function getCheckAnswerData(
  ids: string[],
  components: Pluck<
    GetActivitiesSectionsQuery,
    'getActivitiesSections.components'
  >[]
): CheckAnswerData {
  const checkAnswers: CheckAnswerData = {};

  components.forEach(cmp => {
    if (cmp.type === ACTIVITIES_COMPONENT_TYPE_COLUMNS) {
      const columns = cmp as ComponentColumns;
      columns.columns?.forEach(column => {
        column.components.forEach(cmp => {
          if (!ids.includes(cmp.id)) {
            checkAnswers[cmp.id] = convertComponentToCheckAnswer(cmp);
          }
        });
      });
    } else {
      if (!ids.includes(cmp.id)) {
        checkAnswers[cmp.id] = convertComponentToCheckAnswer(cmp);
      }
    }
  });

  return checkAnswers;
}

export function setComponentAnswer({
  sectionId,
  componentId,
  answer,
  checkAnswerData,
}: {
  sectionId: string;
  componentId: string;
  answer: string | number | boolean | undefined;
  checkAnswerData: CheckAnswerData;
}) {
  const componentAnswer = checkAnswerData[componentId];
  if (checkAnswerData && componentAnswer) {
    checkAnswerVar({
      ...checkAnswerData,
      [componentId]: {
        ...componentAnswer,
        answer,
      },
    });
  }
}

export function setComponentMultiAnswer({
  sectionId,
  componentId,
  answers,
  checkAnswerData,
  hasChecked,
}: {
  sectionId: string;
  componentId: string;
  answers: string[];
  checkAnswerData: CheckAnswerData;
  hasChecked?: boolean;
}) {
  const updatedAnswerData = {
    ...checkAnswerData,
  };
  const componentAnswer = updatedAnswerData[componentId];
  if (!componentAnswer) return;

  const multiAnswer = componentAnswer.multiAnswer;
  checkAnswerVar({
    ...checkAnswerData,
    [componentId]: {
      ...componentAnswer,
      hasChecked: componentAnswer.hasChecked || hasChecked,
      multiAnswer: {
        answerRefs: answers,
        correctLabels: multiAnswer?.correctLabels || [],
        correctRefs: multiAnswer?.correctRefs || [],
      },
    },
  });
}

export function isMultiChoiceAnswerPartiallyCorrect({
  answerRefs,
  correctRefs,
}: {
  answerRefs: string[];
  correctRefs: string[];
}) {
  const isAllCorrect =
    answerRefs.length === correctRefs.length &&
    answerRefs.every(id => correctRefs.includes(id));
  const isAtLeastOneCorrect = answerRefs.some(id => correctRefs.includes(id));
  return isAtLeastOneCorrect && !isAllCorrect;
}

export function isAnswerCorrect(answer: Answer) {
  if (
    answer.multiAnswer &&
    isMultiChoiceAnswerPartiallyCorrect(answer.multiAnswer)
  ) {
    return false;
  }

  if (answer.multiAnswer) {
    return isMultiAnswerCorrect(answer.multiAnswer);
  }
  return isCorrect({
    answer: answer.answer,
    correctAnswer: answer.correctAnswer,
    ignoreCase: answer.ignoreCase,
  });
}

export function getCheckAnswerTitle(answer: Answer) {
  if (
    answer.multiAnswer &&
    isMultiChoiceAnswerPartiallyCorrect(answer.multiAnswer)
  ) {
    return 'Your answer is not fully correct.';
  }
  if (isAnswerCorrect(answer)) {
    return 'Good job! 🎉';
  }
  return 'Don’t give up!';
}

export function resetComponentAnswer(
  id: string,
  checkAnswerData: CheckAnswerData
) {
  const componentAnswers = checkAnswerData[id];
  if (componentAnswers) {
    checkAnswerVar({
      ...checkAnswerData,
      [id]: {
        ...componentAnswers,
        hasChecked: false,
        hasReset: true,
        answer: undefined,
        multiAnswer: {
          answerRefs: [],
          correctRefs: componentAnswers.multiAnswer?.correctRefs || [],
          correctLabels: componentAnswers.multiAnswer?.correctLabels || [],
        },
      },
    });
  }
}
